/**
 * OfflineBanner — sticky banner over AppLayout when offline (Phase 5.1, DES-77).
 *
 * Listens on window.online/offline. No icon dependency — uses plain unicode
 * marker so we don't have to add a fresh asset to /public/icons.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function OfflineBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="fixed top-0 left-0 right-0 z-[80] flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <span aria-hidden className="text-white">⚠</span>
      <span className="font-sans text-chip-label text-white">{t('common.offline')}</span>
    </div>
  );
}
